/**
 * Was ein Tauchgang für Felder hat — einmal beschrieben.
 *
 * Formular und Detailansicht lesen hieraus, in Teilprojekt 3 auch die
 * Tabellenansicht. Ohne gemeinsame Quelle stünden Beschriftungen und
 * Einheiten an drei Stellen und liefen auseinander.
 *
 * Nicht enthalten: alles, was der Tauchcomputer misst (Herzfrequenz, CNS, N2,
 * Kalorien) und alles zur Herkunft (source, content_hash, merged_from). Solche
 * Felder erfasst niemand von Hand — und sie bleiben beim Speichern unberührt.
 */

/** Felder der Flasche: Formularschlüssel -> Schlüssel im tanks-Eintrag. */
export const FLASCHE = {
  tank_volume_l: 'volume_l',
  tank_start_bar: 'start_bar',
  tank_end_bar: 'end_bar',
  tank_gas_type: 'gas_type',
  tank_type: 'tank_type',
  tank_o2_pct: 'o2_pct',
};

export const GRUPPEN = [
  { schluessel: 'kern', titel: 'Tauchgang' },
  { schluessel: 'bedingungen', titel: 'Bedingungen' },
  { schluessel: 'ausruestung', titel: 'Ausrüstung' },
  { schluessel: 'reise', titel: 'Reise' },
];

// `eigenname: true` heisst: Hier steht ein Name, kein Satz. Am Telefon schaltet
// das die Autokorrektur ab und die Grossschreibung auf Wortanfang — „Lopper"
// soll Lopper bleiben und nicht zu „Loppen" werden, und ein Buddy heisst
// „Sandra", nicht „sandra".
export const FELDER = [
  // Kern — was man nach dem Auftauchen notiert.
  { schluessel: 'dive_number', beschriftung: 'Nummer', art: 'zahl', gruppe: 'kern', min: 1 },
  { schluessel: 'start_datum', beschriftung: 'Datum', art: 'datum', gruppe: 'kern' },
  { schluessel: 'start_zeit', beschriftung: 'Uhrzeit', art: 'zeit', gruppe: 'kern' },
  { schluessel: 'dauer_min', beschriftung: 'Dauer', art: 'zahl', gruppe: 'kern',
    einheit: 'min', min: 0, max: 999, schritt: 1 },
  { schluessel: 'max_depth_m', beschriftung: 'Maximaltiefe', art: 'zahl', gruppe: 'kern',
    einheit: 'm', min: 0, max: 300, schritt: 0.1 },
  // Gepflegte Felder: von Hand erfasst, leerbar (siehe Design)
  { schluessel: 'site_name', beschriftung: 'Tauchplatz', art: 'stammsatz', gruppe: 'kern', gepflegt: true , eigenname: true },
  { schluessel: 'buddies', beschriftung: 'Buddys', art: 'namen', gruppe: 'kern', gepflegt: true , eigenname: true },
  { schluessel: 'notes', beschriftung: 'Notiz', art: 'mehrzeilig', gruppe: 'kern', gepflegt: true },
  { schluessel: 'rating', beschriftung: 'Bewertung', art: 'zahl', gruppe: 'kern',
    min: 1, max: 5, schritt: 1, gepflegt: true },

  // Bedingungen
  { schluessel: 'water_temp_avg_c', beschriftung: 'Wassertemperatur', art: 'zahl',
    gruppe: 'bedingungen', einheit: '°C', min: -2, max: 40, schritt: 0.1 },
  { schluessel: 'air_temp_c', beschriftung: 'Lufttemperatur', art: 'zahl',
    gruppe: 'bedingungen', einheit: '°C', min: -30, max: 55, schritt: 0.1 },
  { schluessel: 'visibility_m', beschriftung: 'Sicht', art: 'zahl',
    gruppe: 'bedingungen', einheit: 'm', min: 0, max: 100, schritt: 1 },
  { schluessel: 'visibility_class', beschriftung: 'Sicht (Einstufung)', art: 'auswahl',
    gruppe: 'bedingungen', werte: ['poor', 'medium', 'good', 'excellent'], gepflegt: true },
  { schluessel: 'current', beschriftung: 'Strömung', art: 'auswahl',
    gruppe: 'bedingungen', werte: ['none', 'light', 'moderate', 'strong'] },
  { schluessel: 'surface_cond', beschriftung: 'Oberfläche', art: 'auswahl',
    gruppe: 'bedingungen', werte: ['flat', 'small_waves', 'large_waves'] },
  { schluessel: 'weather', beschriftung: 'Wetter', art: 'text', gruppe: 'bedingungen', gepflegt: true },
  { schluessel: 'water_type', beschriftung: 'Wasser', art: 'auswahl',
    gruppe: 'bedingungen', werte: ['salt', 'fresh', 'brackish'] },
  { schluessel: 'entry_type', beschriftung: 'Einstieg', art: 'auswahl',
    gruppe: 'bedingungen', werte: ['boat', 'shore'] },

  // Ausrüstung
  { schluessel: 'suit_type', beschriftung: 'Anzug', art: 'auswahl',
    gruppe: 'ausruestung', werte: ['wet', 'semidry', 'dry', 'shorty'] },
  { schluessel: 'suit_thickness_mm', beschriftung: 'Anzugdicke', art: 'zahl',
    gruppe: 'ausruestung', einheit: 'mm', min: 0, max: 15, schritt: 0.5 },
  { schluessel: 'weight_kg', beschriftung: 'Blei', art: 'zahl',
    gruppe: 'ausruestung', einheit: 'kg', min: 0, max: 40, schritt: 0.5 },
  { schluessel: 'tank_type', beschriftung: 'Flaschenart', art: 'auswahl',
    gruppe: 'ausruestung', werte: ['aluminum', 'steel'] },
  { schluessel: 'tank_volume_l', beschriftung: 'Flaschengrösse', art: 'zahl',
    gruppe: 'ausruestung', einheit: 'l', min: 0, max: 30, schritt: 0.5 },
  { schluessel: 'tank_start_bar', beschriftung: 'Anfangsdruck', art: 'zahl',
    gruppe: 'ausruestung', einheit: 'bar', min: 0, max: 400, schritt: 1 },
  { schluessel: 'tank_end_bar', beschriftung: 'Enddruck', art: 'zahl',
    gruppe: 'ausruestung', einheit: 'bar', min: 0, max: 400, schritt: 1 },
  { schluessel: 'tank_gas_type', beschriftung: 'Gas', art: 'auswahl',
    gruppe: 'ausruestung', werte: ['air', 'eanx'] },
  { schluessel: 'tank_o2_pct', beschriftung: 'Sauerstoffanteil', art: 'zahl',
    gruppe: 'ausruestung', einheit: '%', min: 21, max: 100, schritt: 1 },

  // Reise
  { schluessel: 'trip_name', beschriftung: 'Reise', art: 'stammsatz', gruppe: 'reise', gepflegt: true , eigenname: true },
  { schluessel: 'boat', beschriftung: 'Boot', art: 'text', gruppe: 'reise', gepflegt: true , eigenname: true },
  { schluessel: 'divemaster', beschriftung: 'Divemaster', art: 'text', gruppe: 'reise', gepflegt: true , eigenname: true },
];

const NACH_SCHLUESSEL = new Map(FELDER.map((f) => [f.schluessel, f]));

export function feld(schluessel) {
  return NACH_SCHLUESSEL.get(schluessel);
}

/**
 * Die Register der Stammdaten-Tabelle.
 *
 * `speicher` sind bewusst blosse Zeichenketten und keine Verweise auf
 * `db.js:SPEICHER` — so bleibt diese Datei ohne Abhängigkeit und lässt sich
 * ohne Datenbank prüfen. Die Werte sind dieselben.
 *
 * `pflicht` beantwortet, was der Filter "nur unvollständige" zeigt. Es sind
 * nicht die Felder, ohne die ein Satz ungültig wäre — sondern die, deren
 * Fehlen Arbeit bedeutet.
 *
 * `nichtLeer` dagegen ist eine Schreibsperre: Ein Stammsatz ohne Namen ist in
 * der Tabelle nicht mehr auffindbar (er sortiert nach ganz oben und trägt eine
 * leere Zeile), und die Tauchgänge finden ihn nicht mehr, weil Buddys als
 * Namen im Tauchgang stehen. Alles andere darf geleert werden.
 */
const sp = (schluessel, beschriftung, art, bearbeitbar, extra = {}) =>
  ({ schluessel, beschriftung, art, bearbeitbar, ...extra });

/**
 * Schlüssel, die es nur im Formular gibt.
 *
 * Datum, Uhrzeit und Dauer werden dort aus `start_utc` und `duration_s`
 * gerechnet — am Tauchgang selbst existieren sie nicht und taugen deshalb
 * nicht als Spalte.
 */
const NUR_FORMULAR = ['start_datum', 'start_zeit', 'dauer_min'];

/** Immer sichtbar, damit die Zeile beim Umschalten erkennbar bleibt. */
const ANKER = [
  // dive_number und site_name werden aus FELDER abgeleitet, um Marken wie
  // 'gepflegt' nicht zweifach zu führen — die Wahrheit ist nur an einer Stelle.
  { ...FELDER.find(f => f.schluessel === 'dive_number'), beschriftung: 'Nr.', bearbeitbar: true, anker: true },
  sp('start_utc', 'Datum', 'anzeige', false, { anker: true }),
  { ...FELDER.find(f => f.schluessel === 'site_name'), beschriftung: 'Platz', bearbeitbar: true, anker: true },
];

/** Gemessenes läuft als gesperrte Anzeige in seiner Gruppe mit. */
const MESSWERTE = [
  sp('max_depth_m', 'Tiefe', 'anzeige', false, { einheit: 'm', gruppe: 'kern' }),
  sp('duration_s', 'Dauer', 'anzeige', false, { gruppe: 'kern' }),
];

function spalteAusFeld(f) {
  const extra = { gruppe: f.gruppe };
  for (const schluessel of ['werte', 'min', 'max', 'schritt', 'einheit', 'gepflegt']) {
    if (f[schluessel] !== undefined) extra[schluessel] = f[schluessel];
  }
  // In einer Tabellenzelle ist mehrzeiliger Text eine gewöhnliche Eingabe.
  const art = f.art === 'mehrzeilig' ? 'text' : f.art;
  return sp(f.schluessel, f.beschriftung, art, true, extra);
}

const ANKER_SCHLUESSEL = ANKER.map((s) => s.schluessel);
const MESSWERTE_SCHLUESSEL = MESSWERTE.map((s) => s.schluessel);

const SPALTEN_TAUCHGAENGE = [
  ...ANKER,
  ...MESSWERTE,
  ...FELDER
    .filter((f) => !NUR_FORMULAR.includes(f.schluessel)
      && !ANKER_SCHLUESSEL.includes(f.schluessel)
      && !MESSWERTE_SCHLUESSEL.includes(f.schluessel))
    .map(spalteAusFeld),
];

export const REGISTER = [
  {
    schluessel: 'sites',
    titel: 'Plätze',
    speicher: 'sites',
    pflicht: ['country', 'koordinaten'],
    spalten: [
      sp('name', 'Name', 'text', true, { nichtLeer: true }),
      sp('country', 'Land', 'text', true),
      sp('region', 'Region', 'text', true),
      sp('water_name', 'Gewässer', 'text', true),
      sp('rating', 'Bewertung', 'zahl', true, { min: 1, max: 5, schritt: 1 }),
      sp('notes', 'Notiz', 'text', true),
      sp('anzahl', 'Tauchgänge', 'zahl', false),
      sp('koordinaten', 'Koordinaten', 'marke', false),
    ],
  },
  {
    schluessel: 'trips',
    titel: 'Reisen',
    speicher: 'trips',
    pflicht: ['country', 'start_date', 'end_date'],
    spalten: [
      sp('name', 'Name', 'text', true, { nichtLeer: true }),
      sp('country', 'Land', 'text', true),
      sp('start_date', 'von', 'datum', true),
      sp('end_date', 'bis', 'datum', true),
      sp('shop', 'Basis', 'text', true),
      sp('anzahl', 'Tauchgänge', 'zahl', false),
    ],
  },
  {
    schluessel: 'buddies',
    titel: 'Buddys',
    speicher: 'buddies',
    pflicht: ['name'],
    spalten: [
      sp('name', 'Name', 'text', true, { nichtLeer: true }),
      sp('anzahl', 'Tauchgänge', 'zahl', false),
    ],
  },
  {
    schluessel: 'gear',
    titel: 'Ausrüstung',
    speicher: 'gear',
    pflicht: ['type', 'manufacturer'],
    spalten: [
      sp('name', 'Name', 'text', true, { nichtLeer: true }),
      sp('type', 'Art', 'text', true),
      sp('manufacturer', 'Hersteller', 'text', true),
      sp('serial', 'Seriennummer', 'text', true),
      sp('purchased', 'gekauft', 'datum', true),
    ],
  },
  {
    schluessel: 'dives',
    titel: 'Tauchgänge',
    speicher: 'dives',
    pflicht: ['dive_number', 'site_name', 'trip_name'],
    spalten: SPALTEN_TAUCHGAENGE,
  },
];

const REGISTER_NACH_SCHLUESSEL = new Map(REGISTER.map((r) => [r.schluessel, r]));

export function register(schluessel) {
  return REGISTER_NACH_SCHLUESSEL.get(schluessel);
}

export function spalte(registerSchluessel, spaltenSchluessel) {
  return register(registerSchluessel)?.spalten
    .find((s) => s.schluessel === spaltenSchluessel);
}
